/**
 * The listing form.
 *
 * Every field the API accepts, in the order a hiring manager thinks about
 * them, validated against the same shared vocabulary the server uses. A salary
 * range is required on every listing.
 *
 * On the posting page the form runs beside a live preview of the row it is
 * writing: the sections and the rail read the same state, so what the company
 * sees is what the validator sees.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import {
  COMMON_BENEFITS,
  COMPENSATION_PERIOD_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  JD_LIMITS,
  LISTING_LIFETIME_DAYS,
  MINIMUM_FIRST_BID_CENTS,
  SENIORITY_LABELS,
  TAXONOMY,
  VISA_SPONSORSHIP_LABELS,
  WORKPLACE_TYPE_LABELS,
  getCategory,
  type Company,
  type EmploymentType,
  type JobInput,
  type Seniority,
  type VisaSponsorship,
  type WorkplaceType,
} from '@bidtohire/shared';

import {
  EMPTY_LOCATION,
  OPTIONS,
  emptyJobForm,
  toJobInput,
  type FieldErrors,
  type JobFormState,
} from '../lib/job-form';
import { compactRange, sectionCompletion } from '../lib/listing-preview';
import { Combobox, hasFinePointer } from './Combobox';
import {
  ChipsField,
  CheckboxGroup,
  MoneyField,
  PlusIcon,
  SelectField,
  StepperField,
  TextAreaField,
  TextField,
} from './Fields';
import { ListingPreview } from './ListingPreview';
import { Money } from './Money';

function benefitLabel(value: string): string {
  return value.replace(/-/g, ' ').replace(/^\w/, (character) => character.toUpperCase());
}

/**
 * A card's heading, with the quiet check that marks it finished. A completed
 * section recedes rather than lighting up: the border softens and the tick
 * draws once.
 */
function SectionHead({
  title,
  done,
  aside,
}: {
  readonly title: string;
  readonly done?: boolean;
  readonly aside?: ReactNode;
}): ReactNode {
  return (
    <div className="sectionhead">
      <h2>{title}</h2>
      {done === true ? (
        <span className="disc is-done is-drawn" aria-label="Section complete">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path
              className="tick"
              d="M2.6 6.2l2.3 2.3L9.4 3.9"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      ) : (
        (aside ?? null)
      )}
    </div>
  );
}

export function JobForm({
  initial,
  submitLabel,
  busy,
  serverError,
  preview,
  footerNote,
  onSubmit,
}: {
  readonly initial?: JobFormState;
  readonly submitLabel: string;
  readonly busy: boolean;
  readonly serverError?: string | undefined;
  /**
   * The line under the submit button. The default describes the posting flow,
   * where saving really does leave a draft for a later payment to publish —
   * which is false, and alarming, when the form is editing a live listing.
   */
  readonly footerNote?: ReactNode;
  /** The posting page's live rail. Absent when the form is used to edit. */
  readonly preview?: {
    readonly company: Pick<Company, 'name' | 'slug'> & { readonly logoKey?: string };
    /**
     * What this listing has already paid. Present on a reservation, where the
     * rail is describing money that is spent rather than money being offered.
     */
    readonly paidCents?: number;
  };
  readonly onSubmit: (input: JobInput) => void | Promise<void>;
}): ReactNode {
  const [state, setState] = useState<JobFormState>(() => initial ?? emptyJobForm());
  const [errors, setErrors] = useState<FieldErrors>({});
  const [attempted, setAttempted] = useState(false);
  const [salaryTouched, setSalaryTouched] = useState(false);

  const subcategories = useMemo(
    () => getCategory(state.category)?.subcategories ?? [],
    [state.category],
  );
  const categoryName = useMemo(() => getCategory(state.category)?.name, [state.category]);
  const finePointer = useMemo(() => hasFinePointer(), []);
  const done = sectionCompletion(state);
  const rangeLabel = compactRange(state);

  function update<K extends keyof JobFormState>(key: K, value: JobFormState[K]): void {
    setState((previous) => {
      const next = { ...previous, [key]: value };
      if (attempted) {
        const result = toJobInput(next);
        setErrors(result.ok ? {} : result.errors);
      }
      return next;
    });
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setAttempted(true);
    const result = toJobInput(state);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    void onSubmit(result.input);
  }

  // One filled end of the range and one empty one is not an error yet — it is
  // an unfinished field, and the empty half says so without a message.
  const halfRange =
    salaryTouched && (state.salaryMin.trim() === '') !== (state.salaryMax.trim() === '');

  const specialismOptions = subcategories.map((subcategory) => ({
    value: subcategory.slug,
    label: subcategory.name,
  }));

  const sections = (
    <>
      <section className={done.role ? 'card formsection is-complete' : 'card formsection'}>
        <SectionHead title="The role" done={done.role} />

        <TextField
          label="Job title"
          value={state.title}
          onChange={(value) => update('title', value)}
          maxLength={JD_LIMITS.titleMaxLength}
          placeholder="Senior Backend Engineer"
          error={errors['title']}
          required
        />

        <div className="fieldrow">
          <div className="col">
            <SelectField
              label="Category"
              value={state.category}
              onChange={(value) => {
                setState((previous) => ({ ...previous, category: value, subcategory: '' }));
              }}
              options={TAXONOMY.map((category) => ({ value: category.slug, label: category.name }))}
              placeholder="Choose a category"
              error={errors['category']}
              required
            />
          </div>
          <div className="col">
            {finePointer ? (
              <Combobox
                label="Specialism"
                value={state.subcategory}
                options={specialismOptions}
                onChange={(value) => update('subcategory', value)}
                placeholder="Choose a specialism"
                emptyPlaceholder="Choose a category first"
                disabled={state.category === ''}
                error={errors['subcategory']}
                hint="Your listing appears on all three boards. A specialism board is never more expensive to lead."
                {...(categoryName === undefined ? {} : { scopeName: categoryName })}
                required
              />
            ) : (
              <SelectField
                label="Specialism"
                value={state.subcategory}
                onChange={(value) => update('subcategory', value)}
                options={specialismOptions}
                placeholder={
                  state.category === '' ? 'Choose a category first' : 'Choose a specialism'
                }
                disabled={state.category === ''}
                error={errors['subcategory']}
                hint="Your listing appears on all three boards. A specialism board is never more expensive to lead."
                required
              />
            )}
          </div>
        </div>

        <TextField
          label="Link to your posting"
          type="url"
          value={state.applyUrl}
          onChange={(value) => update('applyUrl', value)}
          placeholder="https://boards.greenhouse.io/acme/jobs/1234"
          hint="Candidates apply on your own site or applicant tracking system. We never take applications."
          error={errors['applyUrl']}
          required
        />

        <TextField
          label="One-line summary"
          value={state.descriptionSummary}
          onChange={(value) => update('descriptionSummary', value)}
          maxLength={JD_LIMITS.summaryMaxLength}
          showCount
          hint="Shown on leaderboards and in search results."
          error={errors['descriptionSummary']}
        />

        <TextAreaField
          label="Job description"
          value={state.descriptionText}
          onChange={(value) => update('descriptionText', value)}
          rows={14}
          hint="Blank lines separate paragraphs. Lines starting with a dash become a bulleted list."
          error={errors['descriptionText']}
          required
        />
      </section>

      <section className={done.shape ? 'card formsection is-complete' : 'card formsection'}>
        <SectionHead title="Shape of the work" done={done.shape} />

        <CheckboxGroup
          legend="Employment type"
          boxed
          options={OPTIONS.employmentTypes.map((value) => ({
            value,
            label: EMPLOYMENT_TYPE_LABELS[value],
          }))}
          selected={state.employmentType}
          onChange={(values: EmploymentType[]) => update('employmentType', values)}
          error={errors['employmentType']}
        />

        <div className="fieldrow">
          <div className="col">
            <SelectField
              label="Where the work happens"
              value={state.workplaceType}
              onChange={(value: WorkplaceType) => update('workplaceType', value)}
              options={OPTIONS.workplaceTypes.map((value) => ({
                value,
                label: WORKPLACE_TYPE_LABELS[value],
              }))}
              required
            />
          </div>
          <div className="col">
            <SelectField
              label="Level"
              value={state.seniority}
              onChange={(value: Seniority) => update('seniority', value)}
              options={OPTIONS.seniority.map((value) => ({
                value,
                label: SENIORITY_LABELS[value],
              }))}
              placeholder="Choose a level"
              error={errors['seniority']}
              required
            />
          </div>
          <div className="col-sm">
            <TextField
              label="Minimum experience"
              value={state.experienceYearsMin}
              onChange={(value) => update('experienceYearsMin', value)}
              inputMode="numeric"
              hint="Years, optional."
              error={errors['experienceYearsMin']}
            />
          </div>
        </div>

        <fieldset className="boxed">
          <legend>Locations</legend>
          <div className="repeater">
            {state.locations.map((location, index) => (
              <div className="fieldrow" key={index}>
                <div className="col">
                  <TextField
                    label="City"
                    value={location.city}
                    onChange={(value) =>
                      update(
                        'locations',
                        state.locations.map((row, position) =>
                          position === index ? { ...row, city: value } : row,
                        ),
                      )
                    }
                  />
                </div>
                <div className="col">
                  <TextField
                    label="Region"
                    value={location.region}
                    onChange={(value) =>
                      update(
                        'locations',
                        state.locations.map((row, position) =>
                          position === index ? { ...row, region: value } : row,
                        ),
                      )
                    }
                  />
                </div>
                <div className="col-xs">
                  <TextField
                    label="Country"
                    value={location.countryIso2}
                    onChange={(value) =>
                      update(
                        'locations',
                        state.locations.map((row, position) =>
                          position === index ? { ...row, countryIso2: value } : row,
                        ),
                      )
                    }
                    maxLength={2}
                    placeholder="US"
                  />
                </div>
                {state.locations.length > 1 ? (
                  <button
                    type="button"
                    className="linkbtn"
                    onClick={() =>
                      update(
                        'locations',
                        state.locations.filter((_row, position) => position !== index),
                      )
                    }
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            {state.locations.length < JD_LIMITS.maxLocations ? (
              <button
                type="button"
                className="pill pill-quiet pill-sm ghostpill"
                onClick={() => update('locations', [...state.locations, { ...EMPTY_LOCATION }])}
              >
                <PlusIcon />
                Add another location
              </button>
            ) : null}
          </div>
          {errors['locations'] === undefined ? null : (
            <span className="error">{errors['locations']}</span>
          )}
          {state.workplaceType === 'REMOTE' ? (
            <div className="fieldrow">
              <div className="col">
                <TextField
                  label="Remote from"
                  value={state.remoteCountries}
                  onChange={(value) => update('remoteCountries', value)}
                  placeholder="US, CA, GB"
                  hint="Two-letter country codes, separated by commas. Required for remote roles — a search engine drops a remote listing that does not say where it can be worked from."
                  error={errors['remoteCountries']}
                />
              </div>
            </div>
          ) : null}
        </fieldset>
      </section>

      <section className={done.pay ? 'card formsection is-complete' : 'card formsection'}>
        <SectionHead title="Pay" done={done.pay} />
        <p className="small muted">A salary range is required on every listing.</p>

        <div className="fieldrow salaryrow">
          <div className="col">
            <MoneyField
              label="Range from"
              value={state.salaryMin}
              onChange={(value) => update('salaryMin', value)}
              placeholder="120,000"
              invalid={halfRange && state.salaryMin.trim() === ''}
              onBlur={() => setSalaryTouched(true)}
              required
            />
          </div>
          <span className="rangedash" aria-hidden="true">
            –
          </span>
          <div className="col">
            <MoneyField
              label="Range to"
              value={state.salaryMax}
              onChange={(value) => update('salaryMax', value)}
              placeholder="160,000"
              invalid={halfRange && state.salaryMax.trim() === ''}
              onBlur={() => setSalaryTouched(true)}
              required
            />
          </div>
          <div className="col-xs">
            <TextField
              label="Currency"
              value={state.salaryCurrency}
              onChange={(value) => update('salaryCurrency', value)}
              maxLength={3}
            />
          </div>
          <div className="col-sm">
            <SelectField
              label="Period"
              value={state.salaryPeriod}
              onChange={(value) => update('salaryPeriod', value)}
              options={OPTIONS.periods.map((value) => ({
                value,
                label: COMPENSATION_PERIOD_LABELS[value],
              }))}
            />
          </div>
        </div>

        <span className="hint salaryhint" key={rangeLabel === undefined ? 'blank' : 'set'}>
          {rangeLabel === undefined ? (
            'Both ends of the range are shown on the listing.'
          ) : (
            <>
              The board shows this as <span className="mono">{rangeLabel}</span>.
            </>
          )}
        </span>

        <label className="checkfield">
          <input
            type="checkbox"
            checked={state.salaryEstimated}
            onChange={(event) => update('salaryEstimated', event.target.checked)}
          />
          <span>This range is an estimate</span>
        </label>

        {errors['salary'] === undefined ? null : <span className="error">{errors['salary']}</span>}
      </section>

      <section className="card formsection">
        <SectionHead title="Details" aside={<span className="small muted">Optional</span>} />

        <CheckboxGroup
          legend="Benefits"
          boxed
          collapseAfter={6}
          options={COMMON_BENEFITS.map((value) => ({
            value: value as string,
            label: benefitLabel(value),
          }))}
          selected={state.benefits}
          onChange={(values) => update('benefits', values)}
          hint={
            <>
              Choose up to {JD_LIMITS.maxBenefits}. <span className="mono">{state.benefits.length}</span>{' '}
              chosen.
            </>
          }
        />

        <ChipsField
          label="Skills"
          value={state.skills}
          onChange={(value) => update('skills', value)}
          placeholder="Add a skill…"
          limit={JD_LIMITS.maxSkills}
          hint={`Type a comma to add. Up to ${JD_LIMITS.maxSkills}.`}
        />

        <ChipsField
          label="Tags"
          value={state.tags}
          onChange={(value) => update('tags', value)}
          placeholder="Add a tag…"
          limit={JD_LIMITS.maxTags}
          hint={`Type a comma to add. Up to ${JD_LIMITS.maxTags}.`}
        />

        <div className="fieldrow">
          <div className="col">
            <SelectField
              label="Visa sponsorship"
              value={state.visaSponsorship}
              onChange={(value: VisaSponsorship) => update('visaSponsorship', value)}
              options={OPTIONS.visa.map((value) => ({
                value,
                label: VISA_SPONSORSHIP_LABELS[value],
              }))}
            />
          </div>
          <div className="col-sm">
            <StepperField
              label="Openings"
              value={state.openings}
              onChange={(value) => update('openings', value)}
              error={errors['openings']}
            />
          </div>
        </div>
      </section>

      {serverError === undefined ? null : (
        <div className="notice is-error" role="alert">
          {serverError}
        </div>
      )}

      <div className="formactions">
        <button type="submit" className="cta is-compact" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        <span className="note">
          {footerNote ?? (
            <>
              This listing is already paid for, from <Money cents={MINIMUM_FIRST_BID_CENTS} />, so
              submitting publishes it. Listings run {LISTING_LIFETIME_DAYS} days and renew free.
            </>
          )}
        </span>
      </div>
    </>
  );

  if (preview === undefined) {
    return (
      <form className="stack" onSubmit={submit} noValidate>
        {sections}
      </form>
    );
  }

  return (
    <form className="postform" onSubmit={submit} noValidate>
      <div className="postgrid">
        <div className="stack postcol">{sections}</div>
        {/* The phone's pinned bar submits the same form as the button at the
            foot of the page, so it has to say the same thing. A fixed "Save
            and continue" described a flow where a step came after this one.
            None does: submitting publishes. */}
        <ListingPreview
          state={state}
          company={preview.company}
          submitLabel={submitLabel}
          busy={busy}
          {...(preview.paidCents === undefined ? {} : { paidCents: preview.paidCents })}
        />
      </div>
    </form>
  );
}
